import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { namenKern, readBlattNamen } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

export const lektionSolver: Lesson = {
  id: "solver",
  title: "Der Solver",
  summary: "Mehrere Stellschrauben, Nebenbedingungen und ein Ziel – Excel sucht die beste Lösung.",
  durationMinutes: 12,
  learningGoals: [
    "Das Solver-Add-in aktivieren",
    "Zielzelle, veränderbare Zellen und Nebenbedingungen festlegen",
    "Das Solver-Modell mit der Arbeitsmappe speichern",
  ],
  steps: [
    {
      id: "aktivieren",
      title: "Den Solver aktivieren",
      explanation: [
        "Der Solver ist ein mitgeliefertes Add-in, das erst eingeschaltet werden muss: " +
          "„Datei“ → „Optionen“ → „Add-Ins“ → unten „Excel-Add-Ins“ → „Los …“ → Häkchen " +
          "bei „Solver“. Danach steht er auf der Registerkarte „Daten“ ganz rechts.",
        "Wo die Zielwertsuche genau eine Zelle verändert, verändert der Solver mehrere – und " +
          "hält dabei Nebenbedingungen ein: Produktionsmengen, die ganzzahlig und nicht " +
          "negativ sein müssen, ein Budget, das nicht überschritten werden darf.",
      ],
      keyPoints: [
        "Datei → Optionen → Add-Ins → Excel-Add-Ins → Solver.",
        "Mehrere veränderbare Zellen, Nebenbedingungen, Ziel Max/Min/Wert.",
      ],
    },
    {
      id: "modell",
      title: "Ein Solver-Modell",
      explanation: [
        "Ein kleines Beispiel: Zwei Produkte mit Deckungsbeitrag je Stück in B2 und C2, " +
          "Mengen in B3 und C3, Gesamtdeckungsbeitrag in D3 =B2*B3+C2*C3. Maschinenstunden " +
          "je Stück in B4 und C4, verfügbar 100 Stunden.",
        "Im Solver: „Ziel festlegen“ D3, „Max.“, „Durch Ändern von“ B3:C3. Nebenbedingungen " +
          "hinzufügen: B3:C3 >= 0, B3:C3 = ganzzahlig, Stundensumme <= 100. „Lösen“ sucht " +
          "die Mengen mit dem höchsten Deckungsbeitrag.",
        "Das Modell – Ziel, Zellen, Nebenbedingungen – speichert Excel mit dem Blatt in " +
          "versteckten Namen. Wer die Mappe wieder öffnet, findet es im Solver-Dialog vor.",
      ],
      keyPoints: [
        "Ziel festlegen, Max/Min, durch Ändern von …, Nebenbedingungen.",
        "Nebenbedingungen: >= 0, ganzzahlig, Budgetgrenzen.",
        "Das Modell wird mit dem Blatt gespeichert.",
      ],
      task: {
        instruction:
          "Richte ein Solver-Modell mit Zielzelle, mindestens zwei veränderbaren Zellen und " +
          "mindestens einer Nebenbedingung ein und lass es lösen.",
        hint: "Daten → Solver → Ziel, „Durch Ändern von“, „Hinzufügen“ für Nebenbedingungen → Lösen → Solver-Lösung behalten.",
        check: async () => {
          const namen = await readBlattNamen();
          if (namen === null) {
            return nichtPruefbar(
              "Die versteckten Namen, in denen Excel das Solver-Modell speichert",
              "1.4",
              "Öffne den Solver-Dialog: Stehen dort Ziel und Nebenbedingungen, ist das Modell gespeichert.",
              true,
            );
          }
          const solver = namen.filter((name) => namenKern(name.name).startsWith("solver_"));
          const kern = new Set(solver.map((name) => namenKern(name.name)));
          const bedingungen = solver.find((name) => namenKern(name.name) === "solver_num");
          const anzahl = Number((bedingungen?.formula ?? "=0").replace(/^=/, "")) || 0;
          return fromCriteria(
            [
              criterion("Eine Zielzelle ist festgelegt", kern.has("solver_opt")),
              criterion("Veränderbare Zellen sind festgelegt", kern.has("solver_adj")),
              criterion(`Mindestens eine Nebenbedingung (gefunden: ${anzahl})`, anzahl >= 1),
            ],
            {
              success: "Das Solver-Modell ist gespeichert – mit Ziel, Stellschrauben und Grenzen.",
              failure: "Das Solver-Modell ist noch nicht vollständig.",
              hint: "Nach dem Lösen „Solver-Lösung behalten“ wählen, damit Excel das Modell speichert.",
            },
          );
        },
      },
    },
  ],
};
